/**
 * Music files by name (04 §5). Anything dropped into `/music` is found by its file name alone,
 * so adding a track never needs a code change:
 *
 *   title       the title screen and the menus behind it
 *   start       the opening jingle as a game begins, played once
 *   empress     the Empress fight
 *   hiscore     initials entry and the high-score tables
 *   gameover    game over, played once
 *
 * Case, spaces, underscores, a leading track number, a trailing "music" or "theme" and common
 * misspellings do not matter: `01 Game Over.MP3`, `High_Score.ogg` and `Boss Theme.wav` are all
 * found. Where no file answers, the synthesised track plays. Pure: names and lookups.
 */

/** Each word of a file name, as it is often typed -> as it is meant. */
const SPELLING: Readonly<Record<string, string>> = {
  tittle: 'title', titel: 'title', titles: 'title', tilte: 'title', theme: 'title', menu: 'title', main: 'title',
  highscore: 'hiscore', highscores: 'hiscore', hiscores: 'hiscore', hiscor: 'hiscore', scores: 'hiscore',
  gamover: 'gameover', gameovr: 'gameover', gamesover: 'gameover',
  starts: 'start', intro: 'start', opening: 'start', begin: 'start',
  empres: 'empress', emperess: 'empress', emperss: 'empress', boss: 'empress', bossfight: 'empress',
};

/** Whole names after spelling: the multi-word ways of saying a track. */
const PHRASES: Readonly<Record<string, string>> = {
  'title-title': 'title', 'title-screen': 'title', 'title-title-screen': 'title',
  'hi-score': 'hiscore', 'high-score': 'hiscore', 'high-hiscore': 'hiscore', 'hi-hiscore': 'hiscore',
  'game-over': 'gameover',
  'game-start': 'start', 'start-game': 'start', 'player-1': 'start', 'stage-start': 'start',
  'the-empress': 'empress', 'empress-fight': 'empress', 'empress-battle': 'empress',
};

/** Words that only say "this is music" at the end of a name: `Empress Theme`, `Start Jingle`. */
const FILLER = /^(music|theme|track|song|jingle|tune|loop|bgm)$/;

/** `/music/01 Game_Over.MP3` -> `gameover`: lower case, no extension, words joined by hyphens. */
export function trackName(path: string): string {
  const file = path.slice(path.lastIndexOf('/') + 1);
  const dot = file.lastIndexOf('.');
  const bare = (dot > 0 ? file.slice(0, dot) : file).trim().toLowerCase();
  const words = bare.split(/[\s_.-]+/).filter((w) => w !== '');
  // "01 Title", "track 3 - title": a numbering in front is not part of the name.
  while (words.length > 1 && /^(\d+|track)$/.test(words[0] ?? '')) words.shift();
  // "Player 1" is a name; any other trailing word that only says "music" is not.
  while (words.length > 1 && FILLER.test(words[words.length - 1] ?? '')) words.pop();
  const name = words.map((w) => SPELLING[w] ?? w).join('-');
  return PHRASES[name] ?? name;
}

/** Name -> address, from a map of path -> address (what `import.meta.glob` gives). */
export function trackTable(files: Readonly<Record<string, string>>): Map<string, string> {
  const table = new Map<string, string>();
  for (const [path, url] of Object.entries(files)) table.set(trackName(path), url);
  return table;
}
